/* eslint-disable */
import { Button, Global, MantineProvider } from "@mantine/core";
import { NotificationsProvider } from "@mantine/notifications";
import { QueryClientProvider } from "@tanstack/react-query";

import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import React, { useEffect } from "react";
import { BrowserRouter } from "react-router-dom";

import { queryClient } from "lib/react-query";
import storage from "utils/storage";
import { PermissionProvider } from "api/permission/permissionProvider";
import BackgroundWatermark from "components/watermark/watermark";
import SupportFloatingButton from "components/support/SupportFloatingButton";

type AppProviderProps = {
  children: React.ReactNode;
};

function AppProviders({ children }: AppProviderProps) {
  const containerStyle: React.CSSProperties = {
    height: "calc(100vh - 10px)",
    width: "100%",
    overflowY: "auto",
    overflowX: "hidden",
  };
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      if (e.origin !== window.location.origin) return;
      const payload = e.data ?? {};
      if (payload?.type === "IMPERSONATE" && payload?.token) {
        storage.setSessionImpersonationToken(payload.token);
        if (payload?.originalToken && !storage.getOriginalToken()) {
          storage.setOriginalToken(payload.originalToken);
        }

        window.location.reload();
      }
    }

    window.addEventListener("message", onMessage);

    try {
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage(
          { type: "IMPERSONATION_READY" },
          window.location.origin
        );
      }
    } catch (err) {}

    return () => window.removeEventListener("message", onMessage);
  }, []);

  return (
    <MantineProvider
      withCSSVariables
      withNormalizeCSS
      theme={{
        colors: {
          appPrimary: [
            "#0a0d13",
            "#161b24",
            "#202633",
            "#2a3242",
            "#323d51",
            "#465268",
            "#5b677f",
            "#707c96",
            "#8793ad",
            "#9eaac5",
          ],
          cancel: [
            "#f8f9fa",
            "#f1f3f5",
            "#e9ecef",
            "#dee2e6",
            "#ced4da",
            "#adb5bd",
            "#868e96",
            "#495057",
            "#343a40",
            "#212529",
          ],
        },
        primaryColor: "appPrimary",

        // primaryColor: "violet",
        fontSizes: {
          xs: 12,
          sm: 13,
          md: 13,
          lg: 16,
          xl: 18,
        },

        components: {
          // DateRangePicker: {
          //   styles: () => ({
          //     input: {
          //       backgroundColor: "#2a3242",
          //       color: "#fff",
          //       border: "1px solid #2a3242",

          //       "::placeholder": {
          //         color: "#ccc",
          //         opacity: 1,
          //       },

          //       "&:focus": {
          //         borderColor: "#2a3242",
          //       },
          //     },
          //   }),
          // },

          Tabs: {
            styles: (theme) => ({
              tabsList: {
                gap: theme.spacing.md,
              },
              tab: {
                fontWeight: 500,
                padding: "10px 12px",
                borderRadius: theme.radius.sm,
                border: "none",
                color: theme.colors.gray[6],
                "&:hover": {
                  backgroundColor: `${theme.colors.appPrimary[3]} !important`,
                  color: "#fff !important",
                },
                "&[data-active]": {
                  backgroundColor: `${theme.colors.appPrimary[4]} !important`,
                  color: "#fff !important",
                },
              },
              panel: {
                paddingTop: theme.spacing.sm,
              },
            }),
          },

          Badge: {
            defaultProps: {
              variant: "filled",
              fontSize: "13px",
            },
            styles: (theme, params) => {
              return {
                root: {
                  "--tw-bg-opacity": "1",
                  backgroundColor: `${theme.colors.appPrimary[4]} !important`,
                  color: "#fff !important",
                  border: "none",
                },
              };
            },
          },
          Button: {
            defaultProps: {
              size: "md",
            },
            styles: (theme, params) => {
              const isCancel = params.color === "cancel";
              return {
                root: {
                  "--tw-bg-opacity": "1",
                  backgroundColor: isCancel
                    ? `${theme.colors.cancel[2]} !important`
                    : `${theme.colors.appPrimary[4]} !important`,
                  color: isCancel ? "#2f3640 !important" : "#ffffff !important",
                  "&:hover": {
                    backgroundColor: isCancel
                      ? `${theme.colors.cancel[3]} !important`
                      : `${theme.colors.appPrimary[5]} !important`,
                  },
                },
              };
            },
          },
          Title: {
            defaultProps: {
              size: 18,
            },
          },

          Navbar: {
            styles: {
              root: {
                backgroundColor: "#fff !important",
                color: "#000",
              },
            },
          },
          MainHeading: {
            defaultProps: {
              size: 12,
            },
          },
          // Button: {
          //   defaultProps: {
          //     size: "md",
          //   },
          // },
          InputWrapper: {
            styles: {
              label: {
                fontWeight: 700,
              },
            },
          },

          Select: {
            styles: (theme) => ({
              item: {
                "&[data-selected]": {
                  "&, &:hover": {
                    backgroundColor: theme.colors.indigo[1],
                    color: "inherit",
                  },
                },
              },
            }),
          },
          Skeleton: {
            defaultProps: {
              radius: "xl",
            },
          },
        },
      }}
    >
      <Global
        styles={(theme) => ({
          "html, body, #root": {
            fontSize: "13px",
          },

          h1: { fontSize: "18px" },
          h2: { fontSize: "18px" },
          h3: { fontSize: "18px" },
          h4: { fontSize: "18px" },
        })}
      />

      <NotificationsProvider autoClose={3000} position="top-center">
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <PermissionProvider>
              {" "}
              <BackgroundWatermark />
              <div style={containerStyle}>
                {children} <SupportFloatingButton />
              </div>
            </PermissionProvider>
          </BrowserRouter>
          {/* <ReactQueryDevtools initialIsOpen={false} /> */}
        </QueryClientProvider>
      </NotificationsProvider>
    </MantineProvider>
  );
}

export default AppProviders;
